import React, { useContext, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Components
import HeroWithPricingPlans from "../../Components/Layouts/Plans/HeroWithPricingPlans";
import DavidIckeWithContentTwoColumn from "../../Components/Layouts/StaticInfo/DavidIckeWithContentTwoColumn";
import CarouselCardsWithContent from "../../Components/Layouts/Content/Carousels/CarouselCardsWithContent";
import CategoryFeaturedItems from "../../Components/Layouts/Content/Watch/VideoFeed/CategoryFeaturedItems";
import PresenterShowcase from "../../Components/Layouts/StaticInfo/PresenterShowcase";
import AppGrid from "../../Components/Layouts/StaticInfo/AppGrid";
import Accordion from "../../Components/Layouts/StaticInfo/Accordion";
import TripleColumnIcons from "../../Components/Layouts/StaticInfo/TripleColumnIcons";
import TwoColumnWithLatestArticles from "../../Components/Layouts/Content/./Read/TwoColumnWithLatestArticles";

// Context
import {GlobalInterfaceContext} from "../../Context/GlobalInterfaceContext";
import {UserAuthenticationContext} from "../../Context/UserAuthenticationContext";

// Hooks
import useFetchLatestArticles from "../../Hooks/Content/Read/useFetchLatestArticles";
import useFetchSpecificContentItems from "../../Hooks/Content/Watch/VideoSingle/useFetchSpecificContentItems";
import useFetchSubscriptionPlans from "../../Hooks/Plans/useFetchSubscriptionPlans";

// Assets
import UFOSpace from '../../Assets/Images/FrontPage/ufo-space.jpg';
import DNA from '../../Assets/Images/FrontPage/dna.jpg';
import Presenters from '../../Assets/Images/FrontPage/presenters.png';
import Node from '../../Assets/Images/FrontPage/node.jpg';
import David from '../../Assets/Images/FrontPage/david-icke-chair.png';
import Globe from '../../Assets/Images/FrontPage/globe.jpg';
import Pyramids from '../../Assets/Images/FrontPage/pyramids.png';
import Occult from '../../Assets/Images/FrontPage/occult.png';
import Smoke from '../../Assets/Images/FrontPage/decorative-smoke.jpg';
import SeriesBG from '../../Assets/Images/FrontPage/series-category-background.jpg';
import SmokeRed from '../../Assets/Images/FrontPage/smoke-red.jpg';
import SpaceClouds from '../../Assets/Images/FrontPage/space-clouds.jpg';
import HeroStarsDesktop from '../../Assets/Images/FrontPage/hero-stars-flashlight-desktop.jpg';
import HeroStarsMobile from '../../Assets/Images/FrontPage/hero-stars-flashlight-mobile.jpg';
import Sunset from '../../Assets/Images/FrontPage/sunset.jpg';
import {IckonicLogoIcon} from "../../Assets/SVGs/Logos";

// Constants
import {RoutePaths} from "../../Constants/RoutePaths";

// Helpers
import {SetTitle} from "../../Helpers/PageMetadata";
import {getAcquisitionPriceSentence} from "../../Helpers/BillingOffers";

function PreSignUp() {
    const
        globalInterfaceContext = useContext(GlobalInterfaceContext),
        authCtx = useContext(UserAuthenticationContext),

        // User data
        userId = authCtx.userData?.id ?? "1c624ef2-27a9-496f-b518-6f6e612078b6",
        countryCode = "GB",

        navigate = useNavigate(),

        {plans, plansLoading, plansError} = useFetchSubscriptionPlans(),

        {articles, articlesLoading, articlesError} = useFetchLatestArticles(4, 0, false),

        topRatedIds = useMemo(
            () => [
                { type: "Film", id: 3070 },
                { type: "Film",  id: 1026 },
                { type: "Series",  id: 178 },
                { type: "Series", id: 181 }
            ],
            []
        ),
        {
            items: topRated,
            loading: topRatedLoading,
            error: topRatedError
        } = useFetchSpecificContentItems(topRatedIds, countryCode, userId),


        curatedFilmIds = useMemo(
            () => [
                { type: "Film", id: 1163 },
                { type: "Film",  id: 454 },
                { type: "Film",  id: 1542 },
                { type: "Film", id: 1591 }
            ],
            []
        ),
        {
            items: curatedFilms,
            loading: curatedFilmsLoading,
            error: curatedFilmsError
        } = useFetchSpecificContentItems(curatedFilmIds, countryCode, userId),

        curatedSeriesIds = useMemo(
            () => [
                { type: "Series", id: 106 },
                { type: "Series",  id: 128 },
                { type: "Series",  id: 99 },
                { type: "Series", id: 119 }
            ],
            []
        ),
        {
            items: curatedSeries,
            loading: curatedSeriesLoading,
            error: curatedSeriesError
        } = useFetchSpecificContentItems(curatedSeriesIds, countryCode, userId),

        curatedHealthIds = useMemo(
            () => [
                { type: "Series", id: 125 },
                { type: "Series",  id: 132 },
                { type: "Film",  id: 2088 },
                { type: "Series", id: 165 }
            ],
            []
        ),
        {
            items: curatedHealth,
            loading: curatedHealthLoading,
            error: curatedHealthError
        } = useFetchSpecificContentItems(curatedHealthIds, countryCode, userId),

        curatedConsciousnessIds = useMemo(
            () => [
                { type: "Series", id: 113 },
                { type: "Series",  id: 117 },
                { type: "Series",  id: 164 },
                { type: "Series", id: 108 }
            ],
            []
        ),
        {
            items: curatedConsciousness,
            loading: curatedConsciousnessLoading,
            error: curatedConsciousnessError
        } = useFetchSpecificContentItems(curatedConsciousnessIds, countryCode, userId),

        curatedDavidIds = useMemo(
            () => [
                { type: "Series", id: 122 },
                { type: "Film",  id: 2688 },
                { type: "Series",  id: 156 },
                { type: "Series", id: 154 }
            ],
            []
        ),
        {
            items: curatedDavid,
            loading: curatedDavidLoading,
            error: curatedDavidError
        } = useFetchSpecificContentItems(curatedDavidIds, countryCode, userId),

        curatedGlobalIds = useMemo(
            () => [
                { type: "Series", id: 148 },
                { type: "Series",  id: 121 },
                { type: "Series",  id: 182 },
                { type: "Series", id: 107 }
            ],
            []
        ),
        {
            items: curatedGlobal,
            loading: curatedGlobalLoading,
            error: curatedGlobalError
        } = useFetchSpecificContentItems(curatedGlobalIds, countryCode, userId),

        curatedDocsIds = useMemo(
            () => [
                { type: "Film", id: 2190 },
                { type: "Film",  id: 2582 },
                { type: "Film",  id: 2266 },
                { type: "Film", id: 1409 }
            ],
            []
        ),
        {
            items: curatedDocs,
            loading: curatedDocsLoading,
            error: curatedDocsError
        } = useFetchSpecificContentItems(curatedDocsIds, countryCode, userId),

        curatedOccultIds = useMemo(
            () => [
                { type: "Series", id: 150 },
                { type: "Film",  id: 2688 },
                { type: "Series",  id: 163 },
                { type: "Series", id: 123 }
            ],
            []
        ),
        {
            items: curatedOccult,
            loading: curatedOccultLoading,
            error: curatedOccultError
        } = useFetchSpecificContentItems(curatedOccultIds, countryCode, userId);

    if (authCtx.doesAuthTokenExist()) {
        navigate(RoutePaths.Browse);
    }

    useEffect(() => {
        SetTitle("Question Everything");
        window.scrollTo({ top: 0, behavior: "auto" }); // You could also use 'smooth' if you want a scroll animation
    }, []);

    const monthlyPlan = plans?.find((plan) => plan.period === 0);
    const annualPlan = plans?.find((plan) => plan.period === 1);
    const pricingSummary = monthlyPlan && annualPlan
        ? `Monthly subscriptions are ${getAcquisitionPriceSentence(monthlyPlan)}. Annual subscriptions are ${getAcquisitionPriceSentence(annualPlan)}.`
        : "Monthly and annual membership options are available; current prices are shown above.";

    return (
        <div className="page--pre-sign-up">
            <HeroWithPricingPlans
                mainMenuDesktopMode={globalInterfaceContext.mainMenuDesktopMode}
                heading={<h1 className="is-very-loud"><p>See the w{IckonicLogoIcon()}rld <span>Differently</span></p></h1>}
                tagline="Your gateway to alternative media, groundbreaking stories, and unique insights"
                backgroundDesktop={HeroStarsDesktop}
                backgroundMobile={HeroStarsMobile}
                planOneTitleBG="#423370"
                planTwoTitleBG="linear-gradient(90deg, #4676E6 0%, #b760b4 100%)"
                planInnerBG="rgba(66, 51, 112, 0.6)"
                requireTransition={true}
                plans={plans}
                plansLoading={plansLoading}
                plansError={plansError}
            />

            <DavidIckeWithContentTwoColumn/>

            <CarouselCardsWithContent
                isDarkMode={globalInterfaceContext.isDarkMode}
                heading={<h1><span>Top Picks</span> This Month</h1>}
                body={<p>Explore the most-watched content that's sparking conversations</p>}
                carouselItems={topRated}
            />

            <CategoryFeaturedItems
                isDarkMode={globalInterfaceContext.isDarkMode}
                heading="Original Films"
                body={<p>Uncompromising storytelling, challenges the mainstream</p>}
                contentLeftSide={true}
                buttonText="Explore All"
                buttonLink={RoutePaths.Browse}
                buttonTheme={true}
                buttonColor={`168,72,150`}
                buttonColorHover={`137,38,134`}
                focusImage={UFOSpace}
                showFocusImageOnMobile={true}
                bgColor="#010107"
                contentItems={curatedFilmsLoading ? undefined : curatedFilms}
            />

            <CategoryFeaturedItems
                isDarkMode={globalInterfaceContext.isDarkMode}
                heading="Original Series"
                body={<p>Exclusive series exploring perspectives</p>}
                contentLeftSide={false}
                buttonText="Explore All"
                buttonLink={RoutePaths.Browse}
                buttonTheme={true}
                buttonColor={`168,72,150`}
                buttonColorHover={`137,38,134`}
                focusImage={Presenters}
                showFocusImageOnMobile={false}
                bgImage={SeriesBG}
                bgColor="#010107"
                contentItems={curatedSeriesLoading ? undefined : curatedSeries}
            />

            <CategoryFeaturedItems
                isDarkMode={globalInterfaceContext.isDarkMode}
                heading="Health & Wellbeing"
                body={<p>Empowering insights for a balanced mind and body</p>}
                contentLeftSide={true}
                buttonText="Explore All"
                buttonLink={RoutePaths.Category(21)}
                buttonTheme={true}
                buttonColor={`9,133,119`}
                buttonColorHover={`9,104,93`}
                focusImage={DNA}
                showFocusImageOnMobile={true}
                bgColor="#010107"
                contentItems={curatedHealthLoading ? undefined : curatedHealth}
            />

            <CategoryFeaturedItems
                isDarkMode={globalInterfaceContext.isDarkMode}
                heading="Consciousness"
                body={<p>Deep dives into the mysteries of the mind and spirit</p>}
                contentLeftSide={false}
                buttonText="Explore All"
                buttonLink={RoutePaths.Category(5)}
                buttonTheme={false}
                buttonColor={`232,187,39`}
                buttonColorHover={`229,174,44`}
                focusImage={Node}
                showFocusImageOnMobile={true}
                bgColor="#010107"
                contentItems={curatedConsciousnessLoading ? undefined : curatedConsciousness}
            />

            <PresenterShowcase isDarkMode={globalInterfaceContext.isDarkMode} />

            <CategoryFeaturedItems
                isDarkMode={globalInterfaceContext.isDarkMode}
                heading="David Icke"
                body={<p>Exclusive content from the world's most fearless truth-seeker</p>}
                contentLeftSide={true}
                buttonText="Explore All"
                buttonLink={RoutePaths.DavidIcke}
                buttonTheme={true}
                buttonColor={`70,118,230`}
                buttonColorHover={`9,94,182`}
                focusImage={David}
                showFocusImageOnMobile={false}
                bgImage={SpaceClouds}
                bgColor="#010107"
                contentItems={curatedDavidLoading ? undefined : curatedDavid}
            />

            <CategoryFeaturedItems
                isDarkMode={globalInterfaceContext.isDarkMode}
                heading="Global Current Events"
                body={<p>Unfiltered analysis of today's most pressing issues</p>}
                contentLeftSide={false}
                buttonText="Explore All"
                buttonLink={RoutePaths.Browse}
                buttonTheme={true}
                buttonColor={`70,118,230`}
                buttonColorHover={`9,94,182`}
                focusImage={Globe}
                showFocusImageOnMobile={true}
                bgColor="#010107"
                contentItems={curatedGlobalLoading ? undefined : curatedGlobal}
            />

            <CategoryFeaturedItems
                isDarkMode={globalInterfaceContext.isDarkMode}
                heading="Documentaries"
                body={<p>Exclusive content from the world's most fearless truth-seeker</p>}
                contentLeftSide={true}
                buttonText="Explore All"
                buttonLink={RoutePaths.Category(3)}
                buttonTheme={true}
                buttonColor={`130,64,135`}
                buttonColorHover={`105,51,133`}
                focusImage={Pyramids}
                showFocusImageOnMobile={true}
                bgImage={Smoke}
                bgColor="#010107"
                contentItems={curatedDocsLoading ? undefined : curatedDocs}
            />

            <CategoryFeaturedItems
                isDarkMode={globalInterfaceContext.isDarkMode}
                heading="Occult & Hidden Knowledge"
                body={<p>Unfiltered analysis of today's most pressing issues</p>}
                contentLeftSide={false}
                buttonText="Explore All"
                buttonLink={RoutePaths.Category(29)}
                buttonTheme={true}
                buttonColor={`144,27,27`}
                buttonColorHover={`116,23,23`}
                focusImage={Occult}
                showFocusImageOnMobile={false}
                bgImage={SmokeRed}
                bgColor="#010107"
                contentItems={curatedOccultLoading ? undefined : curatedOccult}
            />

            <AppGrid />

            <TwoColumnWithLatestArticles
                articles={articlesLoading ? undefined : articles}
                heading={<h1>Dive into <span>Latest Insights</span></h1>}
                body={<p>The latest articles give you the tools to see the world through a different lens</p>}
            />

            <div
                className="pre-sign-up__finale u-relative_hidden"
                style={{
                    background: `url(${Sunset}) bottom center/cover no-repeat`,
                }}
            >
                <div className="u-full_cover_absolute pre-sign-up__gradient-top" style={{
                    background: 'linear-gradient(180deg, #010107 0%, rgba(32,32,32,0) 100%)',
                    height: 400
                }}/>

                <section className="pre-sign-up__faqs u-relative_hidden">
                    <h1><span>FAQs</span></h1>
                    <Accordion
                        data={{
                            Title: "What Is Ickonic?",
                            Content: <p>Ickonic is the home of news and current affairs with a difference, ground-breaking original series and independent films on topics the mainstream won’t discuss. We are an alternative media platform that brings you uncensored, unbiased and unique information – all in one place.</p>
                        }}
                    />

                    <Accordion
                        data={{
                            Title: "How much does a subscription cost?",
                            Content: <p>{pricingSummary}</p>
                        }}
                    />

                    <Accordion
                        data={{
                            Title: "What types of content are available on Ickonic?",
                            Content: <p>Ickonic produces content that challenge the mainstream of everything. We have daily shows focussing on global current events, podcasts on health, wellness, spirituality and long-form series and documentaries exposing hidden knowledge and the nature of reality.</p>
                        }}
                    />

                    <Accordion
                        data={{
                            Title: "How often is new content added?",
                            Content: <p>New content is added daily.</p>
                        }}
                    />

                    <Accordion
                        data={{
                            Title: "Can I watch Ickonic content on multiple devices?",
                            Content: <p>On an annual plan you can access Ickonic on 2 devices. On a monthly plan only one device can be used at once.</p>
                        }}
                    />
                </section>

                <TripleColumnIcons plans={plans}/>

                <HeroWithPricingPlans
                    mainMenuDesktopMode={globalInterfaceContext.mainMenuDesktopMode}
                    heading={<h1><p>Choose A Plan That Works For You</p></h1>}
                    planOneTitleBG="rgba(255,255,255,0.05)"
                    planTwoTitleBG="rgba(0,0,0,0.2)"
                    planInnerBG="rgba(0,0,0,0.2)"
                    requireTransition={false}
                    plans={plans}
                    plansLoading={plansLoading}
                    plansError={plansError}
                />

                <div className="u-full_cover_absolute pre-sign-up__gradient-bottom" style={{
                    background: 'linear-gradient(0deg, #0f0f0f 0%, rgba(32,32,32,0) 100%)',
                    height: 400
                }}/>
            </div>
        </div>
    );
}

export default PreSignUp;
